<template>
    <div>
        <b-datatable
            ref="grid"
            :key="datatableKey"
            v-model="selectedItems"
            :columns="columns"
            :endpoint="listRoute"
            :hidden-columns="hiddenColumns"
            :initial-filters="initialFilters"
            class="p-0"
            has-filter-selector
            :has-bulk-operations="canBulkAssignOperators()"
            item-key="id"
            :grid-title="gridTitle"
            :filters-expanded="filtersExpanded()"
            :keep-filters="keepFilters"
        >
            <template #bulkOperations="">
                <v-btn
                    v-if="can('api.v1.blood-results.bulk-assign-operators')"
                    :disabled="!canBulkAssignOperators()"
                    color="primary"
                    rounded
                    outlined
                    small
                    @click="openBulkAssignModal()"
                >
                    {{ t('patients::button.assign_operator') }}
                </v-btn>
            </template>

            <template #filters="{ filters }">
                <v-col cols="4">
                    <v-autocomplete
                        v-model="filters.filters.pharmacy_site_id"
                        :clearable="filters.filters.pharmacy_site_id !== null"
                        :items="pharmacySitesOptions"
                        :label="t('patients::form.label.pharmacy_site')"
                        dense
                        multiple
                        outlined
                        rounded
                    ></v-autocomplete>
                </v-col>
                <v-col cols="4">
                    <b-filter-select
                        v-model="filters.filters.provider_id"
                        :label="t('patients::options.label.laboratory_cc_device')"
                        :items="labCCAstmOptions"
                        :clearable="filters.filters.provider_id !== null"
                        multiple
                    ></b-filter-select>
                </v-col>
                <v-col v-if="tab === 0" cols="4">
                    <b-filter-select
                        v-model="filters.filters.rejected"
                        :label="t('patients::options.label.blood_status')"
                        :items="filtersOptions.bloodStatus"
                        :clearable="filters.filters.rejected !== null"
                    ></b-filter-select>
                </v-col>
                <v-col cols="4">
                    <b-filter-select
                        v-model="filters.filters.color_result"
                        :label="t('patients::options.label.blood_result_colors')"
                        :items="filtersOptions.bloodResultColors"
                        :clearable="filters.filters.color_result !== null"
                    ></b-filter-select>
                </v-col>
                <v-col v-if="tab === 0" cols="4">
                    <b-filter-select
                        v-model="filters.filters.operator"
                        :label="t('patients::options.label.operator')"
                        :items="operatorsOptions"
                        :clearable="filters.filters.operator !== null"
                    ></b-filter-select>
                </v-col>
                <v-col cols="4">
                    <b-filter-select
                        v-model="filters.filters.dispensed"
                        :label="t('patients::form.label.dispensed')"
                        :items="filtersOptions.dispensedOptions"
                        :clearable="filters.filters.dispensed !== null"
                    ></b-filter-select>
                </v-col>
                <v-col cols="4">
                    <b-filter-date
                        v-model="filters.filters.date_taken"
                        range
                        :label="t('patients::grid.label.date_taken')"
                        @clear="onCloseDateFilter(filters, 'date_taken')"
                    ></b-filter-date>
                </v-col>
            </template>

            <template #[`item.patient.name`]="{ item }">
                <div v-if="can('patients.get')" class="link" @click="$root.goTo(goToPatint(item))">
                    {{ item.patient.name }}
                </div>
                <div v-else>
                    {{ item.patient.name }}
                </div>
            </template>

            <template #[`item.operator_name`]="{ item }">
                {{ item.operator_name || t('default::label.na') }}
            </template>

            <template #[`item.entered_by`]="{ item }">
                {{ item.entered_by || t('default::label.na') }}
            </template>

            <template #[`item.rejected`]="{ item }">
                <div class="grid-status">
                    <span>
                        {{
                            item.rejected
                                ? t('patients::options.blood_status.rejected')
                                : t('patients::options.blood_status.active')
                        }}
                    </span>
                </div>
            </template>

            <template #[`item.pharmacy_site_name`]="{ item }">
                {{ item.pharmacy_site_name || t('default::label.na') }}
            </template>

            <template #[`item.sample_ref`]="{ item }">
                <div class="link" @click="openDialog(item, 'detail')">
                    {{ item.sample_ref }}
                </div>
            </template>

            <template #[`item.max_dispensing`]="{ item }">
                <div class="grid-status">
                    <div :class="`grid-status-border ${item.max_dispensing ? 'green' : 'red'}`"></div>
                    <span
                        :class="`${item.max_dispensing ? 'green' : 'red'}--text`"
                        v-html="
                            item.max_dispensing ? t('patients::max_dispensing.yes') : t('patients::max_dispensing.no')
                        "
                    ></span>
                </div>
            </template>

            <template #[`item.patient.health_status`]="{ item }">
                <div v-if="healthStatus(item.patient)" class="grid-status">
                    <span>
                        {{ healthStatus(item.patient) }}
                    </span>
                </div>
            </template>

            <template #[`item.actions`]="{ item }">
                <div class="table-actions">
                    <v-tooltip v-if="can('blood-results.export-record-as-pdf')" top transition="v-fab-transition">
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="exportRecordAsPdf(item)"
                            >
                                <v-icon size="24">mdi-file-export-outline</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('default::button.export_record_as_pdf') }}</span>
                    </v-tooltip>

                    <v-tooltip
                        v-if="can('api.v1.patients.dispensing-days.dispensed') && item.dispensable && !unAssignedGrid"
                        top
                        transition="v-fab-transition"
                    >
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="openDialog(item, 'dispensing_days')"
                            >
                                <v-icon>mdi-clipboard-plus-outline</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('patients::buttons.dispensing_days') }}</span>
                    </v-tooltip>

                    <v-tooltip
                        v-if="can('api.v1.patients.blood-results.update') && item.editable && !unAssignedGrid"
                        top
                        transition="v-fab-transition"
                    >
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="$root.goTo(editItemUrl(item))"
                            >
                                <v-icon>mdi-pencil</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('users::button.edit') }}</span>
                    </v-tooltip>

                    <v-tooltip
                        v-if="
                            (can('api.v1.patients.blood-results.show') && !item.editable && !unAssignedGrid) ||
                            (!can('api.v1.patients.blood-results.update') && item.editable && !unAssignedGrid)
                        "
                        top
                        transition="v-fab-transition"
                    >
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="$root.goTo(editItemUrl(item))"
                            >
                                <v-icon>mdi-eye</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('patients::button.details') }}</span>
                    </v-tooltip>

                    <v-tooltip v-if="unAssignedGrid" top transition="v-fab-transition">
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="openDialog(item, 'detail')"
                            >
                                <v-icon>mdi-eye</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('patients::button.details') }}</span>
                    </v-tooltip>

                    <v-tooltip
                        v-if="can('api.v1.blood-results.assign') && unAssignedGrid"
                        top
                        transition="v-fab-transition"
                    >
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="openDialog(item, 'assign')"
                            >
                                <v-icon>mdi-clipboard-account-outline</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('patients::button.assign_to_patient') }}</span>
                    </v-tooltip>
                </div>
            </template>

            <template #[`item.date_taken`]="{ item }">
                {{ item.date_taken ? moment(item.date_taken).format($root.dateFormat) : t('default::label.na') }}
            </template>

            <template #[`item.date_received`]="{ item }">
                {{ item.date_received ? moment(item.date_received).format($root.dateFormat) : t('default::label.na') }}
            </template>

            <template #[`item.color_result`]="{ item }">
                <div v-if="item.color_result" :class="`blood-status ${item.color_result}--text`">
                    <div :class="`status ${item.color_result}--text`">
                        <v-icon>icon icon-blood-result</v-icon>
                    </div>
                    <span v-text="item.color_result"></span>
                </div>
                <div v-else>
                    <span>{{ t('default::label.na') }}</span>
                </div>
            </template>

            <template #[`item.color`]="{ item }">
                <div v-if="item.color" :class="`blood-status ${item.color}--text`">
                    <div :class="`status ${item.color}--text`">
                        <v-icon>icon icon-blood-result</v-icon>
                    </div>
                    <span v-text="item.color"></span>
                </div>
                <div v-else>
                    <span>{{ t('default::label.na') }}</span>
                </div>
            </template>
            <template #[`item.status`]="{ item }">
                <div class="grid-status">
                    <span>
                        {{ item.status }}
                    </span>
                </div>
            </template>

            <template #[`item.is_registered`]="{ item }">
                <div class="grid-status">
                    <div :class="`grid-status-border ${item.is_registered ? 'green' : 'red'}`"></div>
                    <span :class="`${item.is_registered ? 'green' : 'red'}--text`">
                        {{ item.is_registered ? t('default::label.yes') : t('default::label.no') }}
                    </span>
                </div>
            </template>
        </b-datatable>
        <v-dialog v-model="bulkModal" max-width="500px" @click:outside="bulkModal = false">
            <v-card>
                <v-card-title>
                    {{ t('patients::dialog.title.assign_operator') }}
                </v-card-title>
                <v-card-text>
                    <b-autocompleter
                        v-if="availableOperatorsForBulkAssign.length > 0"
                        v-model="selectedOperatorForBulkAssign"
                        :label="t('patients::label.operator')"
                        dense
                        flat
                        outlined
                        rounded
                        :items="availableOperatorsForBulkAssign"
                        item-value="id"
                        item-text="name"
                    >
                    </b-autocompleter>
                    <b-alert
                        v-else
                        :dismissible="false"
                        type="warning"
                        :message="
                            selectedProviderError ? selectedProviderError : t('patients::alert.no_available_operators')
                        "
                    >
                    </b-alert>
                </v-card-text>
                <v-card-actions>
                    <v-btn color="primary" text rounded @click="closeBulkAssignModal()">
                        {{ t('patients::button.cancel') }}
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        :disabled="availableOperatorsForBulkAssign.length < 1 || !selectedOperatorForBulkAssign"
                        color="primary"
                        rounded
                        @click="bulkAssignOperator"
                    >
                        {{ t('patients::button.assign') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog ref="dialog" v-model="patientDialog" max-width="500px" @click:outside="close">
            <v-card>
                <v-card-title class="headline" primary-title
                    >{{ t('patients::dialog.title.select_patient') }}
                </v-card-title>
                <v-card-text>
                    <b-autocompleter
                        v-model="selectedPatient"
                        dense
                        flat
                        outlined
                        rounded
                        :label="t('patients::label.search_patients')"
                        :cache-items="false"
                        hide-selected
                        :items="patinetsList"
                        :search-input.sync="searchPatients"
                        :clearable="selectedPatient !== null"
                        persistent-hint
                        :error-messages="selectedPatientErrors"
                        :hint="t('patients::hint.search_patients')"
                        class="pb-5"
                        @change="selectedPatientErrors = []"
                    ></b-autocompleter>
                </v-card-text>
                <v-card-actions>
                    <v-btn color="primary" text rounded @click="patientDialog = false"
                        >{{ t('patients::button.cancel') }}
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="primary"
                        rounded
                        :disabled="!selectedPatient"
                        :loading="assignBloodResultLoading"
                        @click="assignBloodResult"
                        >{{ t('patients::button.save') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog v-if="selectedBloodResult" ref="detailsDialog" v-model="detailsDialog" max-width="600px">
            <v-card>
                <v-card-title class="headline" primary-title
                    >{{ t('patients::dialog.title.sample_details') }}
                </v-card-title>
                <v-card-text>
                    <v-form readonly disabled class="pt-5 pb-15">
                        <v-row>
                            <v-col v-if="selectedBloodResult.color_result" cols="12" lg="12">
                                <label class="pb-2 pl-1" v-text="t('patients::grid.label.result')"></label>
                                <div :class="`blood-status ${selectedBloodResult.color_result}--text`">
                                    <div :class="`status ${selectedBloodResult.color_result}--text`">
                                        <v-icon>icon icon-blood-result</v-icon>
                                    </div>
                                    <span v-text="selectedBloodResult.color_result"></span>
                                </div>
                            </v-col>
                            <v-col v-if="selectedBloodResult.blood_result_provider" cols="12" lg="6" class="pb-0">
                                <v-text-field
                                    v-model="selectedBloodResult.blood_result_provider.name"
                                    rounded
                                    outlined
                                    dense
                                    :label="t('patients::grid.label.blood_result_provider')"
                                ></v-text-field>
                            </v-col>

                            <v-col v-if="selectedBloodResult.sample_ref" cols="12" lg="6" class="pb-0">
                                <v-text-field
                                    v-model="selectedBloodResult.sample_ref"
                                    rounded
                                    outlined
                                    dense
                                    :label="t('patients::grid.label.sample_id')"
                                ></v-text-field>
                            </v-col>
                            <v-col v-if="selectedBloodResult.pharmacy_site_name" cols="12" lg="6" class="pb-0">
                                <v-text-field
                                    v-model="selectedBloodResult.pharmacy_site_name"
                                    rounded
                                    outlined
                                    dense
                                    :label="t('patients::grid.label.pharmacy_site')"
                                ></v-text-field>
                            </v-col>
                            <v-col v-if="selectedBloodResult.entered_by" cols="12" lg="6" class="pb-0">
                                <v-text-field
                                    v-model="selectedBloodResult.entered_by"
                                    rounded
                                    outlined
                                    dense
                                    :label="t('patients::grid.label.entered_by')"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>
            </v-card>
        </v-dialog>
        <v-dialog
            v-if="selectedBloodResult"
            ref="dialog"
            v-model="dispensingDialog"
            max-width="500px"
            @click:outside="close"
        >
            <v-card>
                <v-card-title class="headline" primary-title>
                    {{ t('patients::dispensing.form.title.add_number_of_days') }}
                </v-card-title>
                <v-card-text>
                    <v-form readonly disabled class="py-5">
                        <v-row>
                            <v-col v-if="selectedBloodResult.color_result" cols="12" lg="12">
                                <label class="pb-2 pl-1" v-text="t('patients::grid.label.result')"></label>
                                <div :class="`blood-status ${selectedBloodResult.color_result}--text`">
                                    <div :class="`status ${selectedBloodResult.color_result}--text`">
                                        <v-icon>icon icon-blood-result</v-icon>
                                    </div>
                                    <span v-text="selectedBloodResult.color_result"></span>
                                </div>
                            </v-col>
                            <v-col v-if="selectedBloodResult.next_due_date" cols="12" lg="6" class="pb-0">
                                <v-text-field
                                    :value="
                                        selectedBloodResult.next_due_date
                                            ? moment(selectedBloodResult.next_due_date).format($root.dateFormat)
                                            : t('default::label.na')
                                    "
                                    rounded
                                    outlined
                                    dense
                                    :label="t('patients::form.label.next_due_date')"
                                ></v-text-field>
                            </v-col>
                            <v-col v-if="selectedBloodResult.validity_date" cols="12" lg="6" class="pb-0">
                                <v-text-field
                                    :value="
                                        selectedBloodResult.validity_date
                                            ? moment(selectedBloodResult.validity_date).format($root.dateFormat)
                                            : t('default::label.na')
                                    "
                                    rounded
                                    outlined
                                    dense
                                    :label="t('patients::form.label.validity_date')"
                                ></v-text-field>
                            </v-col>

                            <v-col v-if="selectedBloodResult.dispensing_days_suggested" cols="12" lg="6" class="pb-0">
                                <v-text-field
                                    v-model="selectedBloodResult.dispensing_days_suggested"
                                    rounded
                                    outlined
                                    dense
                                    :label="t('patients::form.label.dispensing_days_suggested')"
                                ></v-text-field>
                            </v-col>

                            <v-col v-if="selectedBloodResult.dispensing_days_accumulated" cols="12" lg="6" class="pb-0">
                                <v-text-field
                                    v-model="selectedBloodResult.dispensing_days_accumulated"
                                    rounded
                                    outlined
                                    dense
                                    :label="t('patients::form.label.dispensing_days_accumulated')"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                    </v-form>
                    <v-row>
                        <v-col cols="12" class="pb-0">
                            <b-input
                                to-mutate="patients::dispensingForm.max_allowed_dispensing_days"
                                :label="t('patients::dispensing.form.label.max_allowed_dispensing_days')"
                                type="number"
                                :is-number="true"
                                :hint="' '"
                                class="pills large w-100 mw-100 pt-2"
                            ></b-input>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-btn color="primary" text rounded @click="dispensingDialog = false"
                        >{{ t('patients::button.cancel') }}
                    </v-btn>
                    <v-spacer></v-spacer>
                    <b-confirm
                        :text="`${maxDaysToDispense} ${t('patients::dispensing.dispense.title.text')} `"
                        :title="t('patients::dispensing.dispense.title')"
                        @true="dispensDay(selectedBloodResult)"
                    >
                        <template #button="{ on }">
                            <v-btn color="primary" rounded :disabled="!canSendRequest" v-on="on">
                                {{ t('patients::button.dispense') }}
                            </v-btn>
                        </template>
                    </b-confirm>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import BAutocompleter from '../../../../../../bgo/bgo-components/resources/js/components/BAutocompleter.vue';
import BAlert from '../../../../../../bgo/bgo-components/resources/js/components/BAlert.vue';

const DispenseDaysMixin = require(process.env.MIX_APP_ROOT +
    '/modules/patients/resources/js/mixins/DispenseDaysMixin.js').default;
export default {
    components: { BAlert, BAutocompleter },
    mixins: [DispenseDaysMixin],
    props: {
        listRoute: {
            type: String,
            required: true,
        },
        initialFilters: {
            type: Object,
            required: true,
        },
        columns: {
            type: Array,
            required: true,
        },
        hiddenColumns: {
            type: Array,
            required: true,
        },
        unAssignedGrid: {
            type: Boolean,
            required: true,
            default: false,
        },
        gridTitle: {
            type: String,
            required: false,
            default: null,
        },
        tab: {
            type: Number,
            required: false,
            default: 0,
        },
    },
    data() {
        return {
            keepFilters: true,
            datatableKey: 0,
            downloadingFile: false,
            selectedItems: [],
            operators: [],
            bulkModal: false,
            availableOperatorsForBulkAssign: [],
            selectedOperatorForBulkAssign: null,
            selectedProviderError: null,
            filtersOptions: {
                bloodStatus: [
                    {
                        text: t('patients::options.blood_status.all'),
                        value: null,
                    },
                    {
                        text: t('patients::options.blood_status.active'),
                        value: 0,
                    },
                    {
                        text: t('patients::options.blood_status.rejected'),
                        value: 1,
                    },
                ],
                bloodResultColors: [
                    {
                        text: t('patients::options.blood_results_color.all'),
                        value: null,
                    },
                    {
                        text: t('patients::options.blood_results_color.red'),
                        value: 'red',
                    },
                    {
                        text: t('patients::options.blood_results_color.amber'),
                        value: 'amber',
                    },
                    {
                        text: t('patients::options.blood_results_color.green'),
                        value: 'green',
                    },
                ],
                dispensedOptions: [
                    {
                        text: t('patients::options.dispensable.all'),
                        value: null,
                    },
                    {
                        text: t('patients::options.dispensable.yes'),
                        value: 1,
                    },
                    {
                        text: t('patients::options.dispensable.no'),
                        value: 0,
                    },
                ],
                status: [
                    {
                        text: t('users::options.status.all'),
                        value: null,
                    },
                    {
                        text: t('users::options.status.active'),
                        value: 'active',
                    },
                    {
                        text: t('users::options.status.blocked'),
                        value: 'blocked',
                    },
                ],
                max_dispensing: [
                    {
                        text: t('users::options.status.all'),
                        value: null,
                    },
                    {
                        text: t('patients::max_dispensing.yes'),
                        value: 1,
                    },
                    {
                        text: t('patients::max_dispensing.no'),
                        value: 0,
                    },
                ],
                validity: [
                    {
                        text: t('users::options.status.all'),
                        value: null,
                    },
                    {
                        text: t('patients::validity.valid'),
                        value: 1,
                    },
                    {
                        text: t('patients::validity.invalid'),
                        value: 0,
                    },
                ],
                health_status: [
                    {
                        text: t('users::options.status.all'),
                        width: 160,
                        value: null,
                    },
                    {
                        text: t('patients::health_status.na'),
                        value: 'na',
                    },
                    {
                        text: t('patients::health_status.ben'),
                        value: 'ben',
                    },
                    {
                        text: t('patients::health_status.chemo'),
                        value: 'chemo',
                    },
                ],
                is_registered: [
                    {
                        text: t('users::options.status.all'),
                        value: null,
                    },
                    {
                        text: t('consultants::options.registered.yes'),
                        value: 1,
                    },
                    {
                        text: t('consultants::options.registered.no'),
                        value: 0,
                    },
                ],
            },
            detailsDialog: false,
            patientDialog: false,
            searchPatients: null,
            selectedPatientErrors: [],
            selectedPatient: null,
            pharmacySitesOptions: [],
            labCCAstmOptions: [],
            operatorsOptions: [],
            assignBloodResultLoading: false,
        };
    },
    computed: {
        patinetsList() {
            return this.$store.state.patients.bloodResultPatientOptions.map((x) => ({ value: x.id, text: x.name }));
        },
    },
    watch: {
        searchPatients(name) {
            if (!name) {
                this.$store.dispatch('patients/loadBloodResultsPatientOptions', {
                    name: null,
                    pharmacy_site_id: null,
                });
                return false;
            }
            if (name.length < 3) {
                return false;
            }
            this.$store.dispatch('patients/loadBloodResultsPatientOptions', {
                name,
                pharmacy_site_id: this.selectedBloodResult.pharmacy_site_id,
            });
        },
    },
    created() {
        this.loadPharmacySitesOptions();
        this.loadLaboratoryCCASTMDevicesOptions();
        if (this.tab === 0) {
            this.loadOperatorsOptions();
        }

        //If there is operator in the url, we should not keep the filters
        const urlParams = new URLSearchParams(window.location.search);
        const operator = urlParams.get('operator');
        if (operator) {
            this.keepFilters = false;
        }
    },
    mounted() {},
    methods: {
        async exportRecordAsPdf(item) {
            if (this.downloadingFile) {
                return;
            }

            this.downloadingFile = true;

            return await axios
                .get(route('blood-results.export-record-as-pdf', { blood_result: item.id }))
                .then((response) => {
                    if (!response) {
                        return;
                    }
                    const link = document.createElement('a');
                    link.href = response.request.responseURL;
                    document.body.appendChild(link);
                    link.click();
                })
                .catch((error) => {
                    this.$root.notify('error', error.response.data.error);
                })
                .finally(() => {
                    _.delay(() => {
                        this.downloadingFile = false;
                    }, 2000);
                });
        },
        changeTab() {
            this.$refs.grid;
            if (!this.$refs.grid) {
                return;
            }
            this.$refs.grid.switchTab();
        },
        loadPharmacySitesOptions() {
            axios
                .get(
                    route('api.v1.pharmacy-sites.options', {
                        sort: 'name',
                        // allowed_sites: 'provider',
                    })
                )
                .then((response) => {
                    if (!response) {
                        return;
                    }
                    //this.pharmacySitesOptions = response.data.data.map((x) => ({ value: x.id, text: x.name }));
                    this.pharmacySitesOptions = _.union(
                        [
                            {
                                text: t('patients::status.all'),
                                value: null,
                            },
                            {
                                text: t('default::label.na'),
                                value: -1,
                            },
                        ],
                        response.data.data && response.data.data.length
                            ? response.data.data.map((x) => ({ value: x.id, text: x.name }))
                            : []
                    );
                });
        },
        loadLaboratoryCCASTMDevicesOptions() {
            axios
                .get(
                    route('api.v1.providers.options', {
                        sort: 'name',
                        allowed_providers: 'patient',
                    })
                )
                .then((response) => {
                    if (!response) {
                        return;
                    }
                    this.labCCAstmOptions = _.union(
                        [
                            {
                                text: t('patients::status.all'),
                                value: null,
                            },
                        ],
                        response.data.data && response.data.data.length
                            ? response.data.data.map((x) => ({ value: x.id, text: x.name }))
                            : []
                    );
                });
        },
        loadOperatorsOptions() {
            axios
                .get(
                    route('api.v1.operators.options', {
                        sort: 'operator.name',
                        allowed_operators: 'patient',
                    })
                )
                .then((response) => {
                    if (!response) {
                        return;
                    }
                    this.operatorsOptions = _.union(
                        [
                            {
                                text: t('patients::status.all_from_pharmacy_sites'),
                                value: null,
                            },
                            {
                                text: 'Unassigned',
                                value: -1,
                            },
                        ],
                        response.data.data && response.data.data.length
                            ? response.data.data.map((x) => ({ value: x.operator.id, text: x.operator.name }))
                            : []
                    );
                });
        },
        editItemUrl(item) {
            return route('patients.get', {
                patient: item.patient.id,
                load_lood_result: true,
                blood_result_id: item.id,
            });
        },
        goToPatint(item) {
            return route('patients.get', {
                patient: item.patient.id,
            });
        },
        assignBloodResult() {
            if (!this.selectedPatient) {
                this.selectedPatient = null;
                this.selectedBloodResult = null;
                this.$store.dispatch('patients/clearBloodResultsPatientOptions');
                return val;
            }
            this.assignBloodResultLoading = true;
            axios({
                method: 'put',
                url: route('api.v1.blood-results.assign', { blood_result: this.selectedBloodResult.id }),
                data: { patient_id: this.selectedPatient },
            })
                .then(() => {
                    this.datatableKey++;
                    this.patientDialog = false;
                    this.selectedPatient = null;
                    this.selectedBloodResult = null;
                    this.selectedPatientErrors = [];
                    this.$store.dispatch('patients/clearBloodResultsPatientOptions');
                    this.$root.notify('info', t('patients::notification.assign_to_patient.success'));
                })
                .catch((error) => {
                    if (!error || !error.response) {
                        return;
                    }
                    this.selectedPatientErrors = error.response.data.error;
                    this.$root.notify('error', error.response.data.error);
                })
                .finally(() => _.delay(() => (this.assignBloodResultLoading = false), 400));
        },
        healthStatus(item) {
            if (item && item.health_status) {
                return _.map(item.health_status, (status) => {
                    return t('patients::health_status.' + status);
                }).join(', ');
            }
            return false;
        },
        onCloseDateFilter(filters, filter) {
            switch (filter) {
                case 'date_taken':
                    filters.filters.date_taken = null;
                    break;
                default:
                    break;
            }
        },
        filtersExpanded() {
            if (this.initialFilters.operator !== null) {
                return true;
            }

            return false;
        },
        canBulkAssignOperators() {
            return (
                this.$store.state.data_table.filters.operator == -1 &&
                this.$store.state.data_table.filters.pharmacy_site_id !== null &&
                this.$store.state.data_table.filters.pharmacy_site_id?.length === 1 &&
                can('api.v1.blood-results.bulk-assign-operators')
            );
        },
        openBulkAssignModal() {
            this.availableOperatorsForBulkAssign = [];
            this.loadAvailableOperatorsForBulkAssign();
        },
        closeBulkAssignModal() {
            this.bulkModal = false;
            this.selectedOperatorForBulkAssign = null;
            this.availableOperatorsForBulkAssign = [];
        },
        bulkAssignOperator() {
            this.bulkModal = false;
            const ids = this.selectedItems.map((item) => item.id);

            axios
                .post(route('api.v1.blood-results.bulk-assign-operators'), {
                    operator_id: this.selectedOperatorForBulkAssign,
                    blood_results: ids,
                })
                .then(() => {
                    this.$root.notify('info', t('patients::notification.bulk_assign_operators.success'));
                })
                .catch((error) => {
                    this.$root.notify('error', error.response.data.error);
                })
                .finally(() => {
                    this.selectedOperatorForBulkAssign = null;
                    this.availableOperatorsForBulkAssign = [];
                    this.datatableKey++;
                });
        },
        loadAvailableOperatorsForBulkAssign() {
            if (
                !this.$store.state.data_table.filters.pharmacy_site_id ||
                this.$store.state.data_table.filters.pharmacy_site_id.length !== 1
            ) {
                return;
            }

            axios
                .get(
                    route('api.v1.operators.available-operators', {
                        pharmacy_site: this.$store.state.data_table.filters.pharmacy_site_id[0],
                    })
                )
                .then((response) => {
                    this.availableOperatorsForBulkAssign = response.data.data;
                    this.selectedProviderError = null;
                })
                .catch((error) => {
                    this.selectedProviderError = error.response.data.error;
                })
                .finally(() => {
                    this.bulkModal = true;
                });
        },
    },
};
</script>

<style></style>
